import React from "react";
import { Input, Select, SelectOption } from "@showitinc/showit-wc";
import { fromJS } from "immutable";

import When from "./utils/When";
import AssetSelectInput from "../app2components/panel/shared/AssetSelectInput";
import AppConstants from "../constants/AppConstants";
import GradientProperties from "./GradientProperties";
import { elementSyncUtils } from "../utils/SyncUtils";
import { gatherProps } from "../utils/Merge/MergeUtils.mjs";
import { formatPropUpdate } from "../utils/PropertyUtils";
import { clearUnusedProps } from "../utils/BackgroundUtils";
import SplitMergeWrapper from "../app2components/base/SplitMergeWrapper";
import { INPUT_TYPE_NUMBER } from "../constants/ElementConstants";
import { initFormatter } from "../utils/PropertyFormatters";
import ColorPicker from "../app2components/base/ColorPicker";
import LeftOffset from "./base/LeftOffset";

const {
  ALLOWED_BG_IMAGE_EXTS,
  BG_PROPERTY_BG_COLOR,
  BG_PROPERTY_FILL_TYPE,
  BG_PROPERTY_MEDIA_TYPE,
  BG_PROPERTY_LOOP,
  BG_PROPERTY_LOOP_PLAY_ONCE,
  BG_PROPERTY_LOOP_REPEAT,
  BG_PROPERTY_OPACITY,
  BG_PROPERTY_POSITION,
  BG_PROPERTY_POSITION_BOTTOM_CENTER,
  BG_PROPERTY_POSITION_BOTTOM_LEFT,
  BG_PROPERTY_POSITION_BOTTOM_RIGHT,
  BG_PROPERTY_POSITION_MIDDLE_CENTER,
  BG_PROPERTY_POSITION_MIDDLE_LEFT,
  BG_PROPERTY_POSITION_MIDDLE_RIGHT,
  BG_PROPERTY_POSITION_TOP_CENTER,
  BG_PROPERTY_POSITION_TOP_LEFT,
  BG_PROPERTY_POSITION_TOP_RIGHT,
  BG_PROPERTY_SCALE,
  BG_PROPERTY_SCALE_COVER,
  BG_PROPERTY_SCALE_FIT,
  BG_PROPERTY_SCALE_NATIVE,
  BG_PROPERTY_SCROLL,
  BG_PROPERTY_SCROLL_FIXED,
  BG_PROPERTY_SCROLL_PARALLAX,
  BG_PROPERTY_SCROLL_W_PAGE,
  BG_PROPERTY_IMAGE,
  BG_PROPERTY_VIDEO,
  BG_TYPE_COLOR,
  BG_TYPE_IMAGE,
  BG_TYPE_GRADIENT,
  BG_PROPERTY_GRADIENT_COLOR_1,
  BG_PROPERTY_GRADIENT_COLOR_2,
  BG_PROPERTY_GRADIENT_ROTATION,
  BG_TYPE_NONE,
  BG_TYPE_VIDEO,
  LAYOUT_MOBILE,
  MIN_OPACITY,
  MAX_OPACITY,
} = AppConstants;

const defaults = fromJS({
  [BG_PROPERTY_LOOP]: BG_PROPERTY_LOOP_PLAY_ONCE,
  [BG_PROPERTY_SCALE]: BG_PROPERTY_SCALE_COVER,
  [BG_PROPERTY_POSITION]: BG_PROPERTY_POSITION_MIDDLE_CENTER,
  [BG_PROPERTY_SCROLL]: BG_PROPERTY_SCROLL_W_PAGE,
  [BG_PROPERTY_OPACITY]: MAX_OPACITY,
});

const BackgroundProperties = ({
  imageAllowed = true,
  videoAllowed = true,
  wpFeaturedAllowed = false,
  scrollEnabled = true,
  onPropUpdate,
  layout,
  parent,
}) => {
  const _onPropChange = (propInfo) => {
    clearUnusedProps(propInfo, onPropUpdate);
    onPropUpdate && onPropUpdate(propInfo);
  };

  const { isSynced, toggleSyncedProp, getPropName } = elementSyncUtils(
    parent,
    _onPropChange,
    layout
  );

  const syncComponent = defaults.mergeDeep(
    gatherProps(parent, layout, [BG_PROPERTY_FILL_TYPE, BG_PROPERTY_MEDIA_TYPE], true)
  );

  const PALETTE_PREFIX = "colors-";
  const mediaType = syncComponent.get(BG_PROPERTY_MEDIA_TYPE);
  const isVideo = mediaType === BG_TYPE_VIDEO;
  const fillType = syncComponent?.get(BG_PROPERTY_FILL_TYPE, BG_TYPE_COLOR);

  const addToggle = (propName) => ({
    sync: isSynced(propName),
    toggleSync: () => {
      toggleSyncedProp(propName);
    },
  });

  const { onChange: onFillChange } = initFormatter(_onPropChange);
  const { onChange: onGradientChange } = initFormatter(
    _onPropChange,
    getPropName,
    BG_PROPERTY_FILL_TYPE
  );

  const fillColorPropName = getPropName(BG_PROPERTY_BG_COLOR, BG_PROPERTY_FILL_TYPE);
  const fillColor = syncComponent?.get(BG_PROPERTY_BG_COLOR, `${PALETTE_PREFIX}0`);

  const handleFillTypeChange = (e) => {
    const newFillType = e.target.value;
    const updates = [formatPropUpdate(getPropName(BG_PROPERTY_FILL_TYPE), newFillType, fillType)];

    // Get color values based on fill type transition
    if (newFillType === BG_TYPE_GRADIENT) {
      const solidColor = syncComponent?.get(BG_PROPERTY_BG_COLOR, `${PALETTE_PREFIX}0`);
      updates.push(
        formatPropUpdate(
          getPropName(BG_PROPERTY_GRADIENT_COLOR_1, BG_PROPERTY_FILL_TYPE),
          solidColor,
          null
        ),
        formatPropUpdate(
          getPropName(BG_PROPERTY_GRADIENT_COLOR_2, BG_PROPERTY_FILL_TYPE),
          solidColor,
          null
        ),
        formatPropUpdate(getPropName(BG_PROPERTY_GRADIENT_ROTATION, BG_PROPERTY_FILL_TYPE), 0, null)
      );
    } else if (newFillType === BG_TYPE_COLOR) {
      const gradientColor = syncComponent?.get(BG_PROPERTY_GRADIENT_COLOR_1, `${PALETTE_PREFIX}0`);
      updates.push(
        formatPropUpdate(
          getPropName(BG_PROPERTY_BG_COLOR, BG_PROPERTY_FILL_TYPE),
          gradientColor,
          null
        )
      );
    }

    updates.forEach(_onPropChange);
  };

  return (
    <>
      <When it={imageAllowed || videoAllowed || wpFeaturedAllowed}>
        <SplitMergeWrapper {...addToggle(BG_PROPERTY_MEDIA_TYPE)} layout={layout}>
          <Select
            label="Media Type"
            value={mediaType}
            context
            reserveContext
            shift="left"
            onSISelect={(e) =>
              _onPropChange(
                formatPropUpdate(getPropName(BG_PROPERTY_MEDIA_TYPE), e.target.value, mediaType)
              )
            }
          >
            <SelectOption value={BG_TYPE_NONE}>None</SelectOption>
            {imageAllowed && <SelectOption value={BG_TYPE_IMAGE}>Image</SelectOption>}
            {videoAllowed && <SelectOption value={BG_TYPE_VIDEO}>Video</SelectOption>}
            {wpFeaturedAllowed && (
              <SelectOption value="wpFeaturedImage">WordPress Featured Image</SelectOption>
            )}
          </Select>
          <When it={[BG_TYPE_IMAGE, BG_TYPE_VIDEO].includes(mediaType)}>
            <LeftOffset>
              {isVideo && (
                <AssetSelectInput
                  label="Video"
                  propName={getPropName(BG_PROPERTY_VIDEO, BG_PROPERTY_MEDIA_TYPE)}
                  selectLabel="Choose Video"
                  enableDelete
                  assetType={BG_TYPE_VIDEO}
                  value={syncComponent.get(BG_PROPERTY_VIDEO, false)}
                  helpTooltip="bg-video"
                  onChange={_onPropChange}
                />
              )}
              <AssetSelectInput
                label={`${isVideo ? "Fallback " : ""}Image`}
                propName={getPropName(BG_PROPERTY_IMAGE, BG_PROPERTY_MEDIA_TYPE)}
                selectLabel="Choose Image"
                enableDelete
                allowStockPhotos
                extensionFilter={ALLOWED_BG_IMAGE_EXTS}
                value={syncComponent.get(BG_PROPERTY_IMAGE, false)}
                onChange={_onPropChange}
              />
              {isVideo && (
                <Select
                  label="Duration"
                  reserveContext
                  shift="left"
                  value={syncComponent.get(BG_PROPERTY_LOOP)}
                  onSISelect={(e) =>
                    _onPropChange(
                      formatPropUpdate(
                        getPropName(BG_PROPERTY_LOOP, BG_PROPERTY_MEDIA_TYPE),
                        e.target.value,
                        syncComponent.get(BG_PROPERTY_LOOP)
                      )
                    )
                  }
                >
                  <SelectOption value={BG_PROPERTY_LOOP_PLAY_ONCE}>Play Once</SelectOption>
                  <SelectOption value={BG_PROPERTY_LOOP_REPEAT}>Loop</SelectOption>
                </Select>
              )}
              <Select
                label="Size"
                value={syncComponent.get(BG_PROPERTY_SCALE)}
                reserveContext
                shift="left"
                onSISelect={(e) =>
                  _onPropChange(
                    formatPropUpdate(
                      getPropName(BG_PROPERTY_SCALE, BG_PROPERTY_MEDIA_TYPE),
                      e.target.value,
                      syncComponent.get(BG_PROPERTY_SCALE)
                    )
                  )
                }
              >
                <SelectOption value={BG_PROPERTY_SCALE_COVER}>Fill</SelectOption>
                <SelectOption value={BG_PROPERTY_SCALE_FIT}>Contain</SelectOption>
                <SelectOption value={BG_PROPERTY_SCALE_NATIVE}>Don&apos;t Scale</SelectOption>
              </Select>
              <Select
                label="Position"
                reserveContext
                shift="left"
                value={syncComponent.get(BG_PROPERTY_POSITION)}
                onSISelect={(e) =>
                  _onPropChange(
                    formatPropUpdate(
                      getPropName(BG_PROPERTY_POSITION, BG_PROPERTY_MEDIA_TYPE),
                      e.target.value,
                      syncComponent.get(BG_PROPERTY_POSITION)
                    )
                  )
                }
              >
                <SelectOption value={BG_PROPERTY_POSITION_MIDDLE_CENTER}>Middle Center</SelectOption>
                <SelectOption value={BG_PROPERTY_POSITION_TOP_LEFT}>Top Left</SelectOption>
                <SelectOption value={BG_PROPERTY_POSITION_TOP_CENTER}>Top Center</SelectOption>
                <SelectOption value={BG_PROPERTY_POSITION_TOP_RIGHT}>Top Right</SelectOption>
                <SelectOption value={BG_PROPERTY_POSITION_MIDDLE_LEFT}>Middle Left</SelectOption>
                <SelectOption value={BG_PROPERTY_POSITION_MIDDLE_RIGHT}>Middle Right</SelectOption>
                <SelectOption value={BG_PROPERTY_POSITION_BOTTOM_LEFT}>Bottom Left</SelectOption>
                <SelectOption value={BG_PROPERTY_POSITION_BOTTOM_CENTER}>Bottom Center</SelectOption>
                <SelectOption value={BG_PROPERTY_POSITION_BOTTOM_RIGHT}>Bottom Right</SelectOption>
              </Select>
              {scrollEnabled && (
                <Select
                  label="Scroll"
                  value={syncComponent.get(BG_PROPERTY_SCROLL)}
                  reserveContext
                  shift="left"
                  onSISelect={(e) =>
                    _onPropChange(
                      formatPropUpdate(
                        getPropName(BG_PROPERTY_SCROLL, BG_PROPERTY_MEDIA_TYPE),
                        e.target.value,
                        syncComponent.get(BG_PROPERTY_SCROLL)
                      )
                    )
                  }
                >
                  <SelectOption value={BG_PROPERTY_SCROLL_W_PAGE}>Scroll with page</SelectOption>
                  {layout !== LAYOUT_MOBILE && (
                    <SelectOption value={BG_PROPERTY_SCROLL_PARALLAX}>Parallax</SelectOption>
                  )}
                  <SelectOption value={BG_PROPERTY_SCROLL_FIXED}>Fixed</SelectOption>
                </Select>
              )}
              <Input
                type={INPUT_TYPE_NUMBER}
                min={MIN_OPACITY.toString()}
                max={MAX_OPACITY.toString()}
                step="10"
                label="Opacity"
                units="%"
                value={syncComponent.get(BG_PROPERTY_OPACITY)}
                onSIBeforeChange={(e) =>
                  _onPropChange(
                    formatPropUpdate(
                      getPropName(BG_PROPERTY_OPACITY, BG_PROPERTY_MEDIA_TYPE),
                      e.target.value,
                      syncComponent.get(BG_PROPERTY_OPACITY)
                    )
                  )
                }
                onSIInput={(e) =>
                  _onPropChange(
                    formatPropUpdate(
                      getPropName(BG_PROPERTY_OPACITY, BG_PROPERTY_MEDIA_TYPE),
                      e.target.value,
                      syncComponent.get(BG_PROPERTY_OPACITY),
                      null,
                      false
                    )
                  )
                }
              />
            </LeftOffset>
          </When>
        </SplitMergeWrapper>
      </When>

      <SplitMergeWrapper {...addToggle(BG_PROPERTY_FILL_TYPE)} layout={layout}>
        <Select
          context
          label="Fill"
          onSISelect={handleFillTypeChange}
          reserveContext
          shift="left"
          value={fillType}
        >
          <SelectOption value={BG_TYPE_COLOR}>Color</SelectOption>
          <SelectOption value={BG_TYPE_GRADIENT}>Gradient</SelectOption>
        </Select>
        <When it={[BG_TYPE_COLOR, BG_TYPE_IMAGE, BG_TYPE_VIDEO].includes(fillType) || fillType === BG_TYPE_GRADIENT}>
          <LeftOffset>
            <When it={[BG_TYPE_COLOR, BG_TYPE_IMAGE, BG_TYPE_VIDEO].includes(fillType)}>
              <ColorPicker
                label="Color"
                id={`bg-color-${parent.get("type")}`}
                color={fillColor}
                onSIBeforeChange={onFillChange(fillColorPropName, fillColor)}
                onSIChange={onFillChange(fillColorPropName, fillColor)}
                onSIInput={onFillChange(fillColorPropName, fillColor, false)}
              />
            </When>
            <When it={fillType === BG_TYPE_GRADIENT}>
              <GradientProperties
                background
                element={syncComponent}
                fallbackColor={fillColor}
                onChange={onGradientChange}
              />
            </When>
          </LeftOffset>
        </When>
      </SplitMergeWrapper>
    </>
  );
};

export default BackgroundProperties;
